import { useParams } from "react-router-dom";
import { MessagesSquare, EyeOff, Eye, Trash2, MessageCircleQuestion } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import { Section, Loadable, Empty, Status, Info, History, ActionButton, RowLink, BackLink, useLoad, fmt } from "@/components/control/kit";
import { audienceText } from "@/pages/CommunityPosts";

const MOD_LABEL = { hide: "Ausgeblendet", restore: "Wiederhergestellt", remove: "Entfernt", ask: "Rückfrage gesendet" };
const ACTIONS = [
  { action: "hide", label: "Ausblenden", icon: EyeOff, when: ["sichtbar"], danger: true },
  { action: "restore", label: "Wiederherstellen", icon: Eye, when: ["ausgeblendet"] },
  { action: "remove", label: "Entfernen", icon: Trash2, when: ["sichtbar", "ausgeblendet"], danger: true },
  { action: "ask", label: "Rückfrage", icon: MessageCircleQuestion, when: ["sichtbar", "ausgeblendet"] },
];

const history = (items) => (items || []).map((m) => ({ action: MOD_LABEL[m.action] || m.action, at: m.at, by: m.by?.name, by_role: m.by?.role, reason: m.reason }));

export function ModButtons({ item, path, prefix, reload }) {
  return ACTIONS.filter((a) => a.when.includes(item.status)).map((a) => (
    <ActionButton key={a.action} label={a.label} icon={a.icon} danger={a.danger} testId={`${prefix}-${a.action}`} onDone={reload}
      submitLabel={a.label} title={`${a.label} – begründen`}
      description={a.action === "ask" ? "Die verfassende Person erhält die Rückfrage als Benachrichtigung." : "Die verfassende Person wird neutral benachrichtigt."}
      fields={[
        { name: "reason", label: "Begründung (intern, mit Bezug auf die Gruppenregeln)", type: "textarea", required: true },
        { name: "message", label: a.action === "ask" ? "Rückfrage an die verfassende Person" : "Zusätzliche Nachricht an die verfassende Person (optional)", type: "textarea", required: a.action === "ask" },
      ]}
      onSubmit={async (v) => { await api.post(path, { action: a.action, ...v }); return `${MOD_LABEL[a.action]}.`; }} />
  ));
}

function Comments({ d, reload }) {
  if (d.comments.length === 0) return <Empty text="Keine Kommentare." />;
  return (
    <ul className="divide-y divide-border" data-testid="community-comments">
      {d.comments.map((c) => (
        <li key={c.id} className="py-3" data-testid={`community-comment-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-brand-purple">{c.author?.name} · <span className="font-normal text-muted-foreground">{fmt(c.created_at)}{c.edited_at ? " · bearbeitet" : ""}</span></p>
            <Status value={c.status} />
          </div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-800">{c.body || "(Text entfernt)"}</p>
          {(c.moderation || []).map((m, i) => (
            <p key={i} className="mt-1 text-xs text-muted-foreground" data-testid={`comment-${c.id}-moderation-${i}`}>
              {MOD_LABEL[m.action] || m.action} · {fmt(m.at)} · {m.by?.name} · Begründung: {m.reason}
            </p>
          ))}
          {d.can_moderate && <div className="mt-2 flex flex-wrap gap-2"><ModButtons item={c} path={`/community/comments/${c.id}/moderate`} prefix={`comment-${c.id}`} reload={reload} /></div>}
        </li>
      ))}
    </ul>
  );
}

function PostView({ d, reload }) {
  const p = d.post;
  return (
    <>
      <PageHeader title="Community-Beitrag" subtitle={`${p.author?.name || "–"} · ${fmt(p.created_at)}`} icon={MessagesSquare}
        actions={<><Status value={p.status} testId="community-post-status" />{d.can_moderate && <ModButtons item={p} path={`/community/posts/${p.id}/moderate`} prefix="post" reload={reload} />}</>} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Beitrag" testId="community-post">
          <p className="whitespace-pre-wrap text-sm text-zinc-800" data-testid="community-post-body">{p.body || "(Text von der verfassenden Person entfernt)"}</p>
          <div className="mt-4"><Info items={[["Zielgruppe", audienceText(p.audience)], p.edited_at && ["Zuletzt bearbeitet", `${fmt(p.edited_at)} (${p.edits} Änderung(en))`]]} /></div>
        </Section>
        <Section title="Meldungen zu diesem Beitrag" testId="community-reports">
          {d.reports.length === 0 ? <Empty text="Keine Meldungen." /> : (
            <ul className="divide-y divide-border text-sm">
              {d.reports.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 py-2">
                  <RowLink to={`/meldungen-sicherheit/${r.id}`} testId={`community-report-${r.id}`}>{r.reason}{r.comment_id ? " (Kommentar)" : ""}</RowLink>
                  <Status value={r.status} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Section title={`Kommentare (${d.comments.filter((c) => c.status === "sichtbar").length} sichtbar / ${d.comments.length} gesamt)`} testId="community-comments-section"><Comments d={d} reload={reload} /></Section>
        <Section title="Moderationsverlauf" testId="community-history"><History items={history(p.moderation)} /></Section>
      </div>
    </>
  );
}

export default function CommunityPostDetail() {
  const { id } = useParams();
  const st = useLoad(`/community/posts/${id}`);
  return (
    <div>
      <BackLink to="/community-beitraege">Zurück zu Community-Beiträgen</BackLink>
      <Loadable state={st}>{(d) => <PostView d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
